import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageMeta from "@/components/feature/PageMeta";
import PosterGrid from "@/components/base/PosterGrid";
import SectionHeader from "@/components/base/SectionHeader";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import { getTitleById, type Title } from "@/mocks/titles";
import { getRelated } from "@/utils/catalog";
import { useFavorites } from "@/hooks/useFavorites";

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-2 text-[13px]">
      <dt className="w-14 shrink-0 text-background-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-background-100">{value}</dd>
    </div>
  );
}

function MissingDetail() {
  return (
    <Layout>
      <PageMeta
        title="影片不存在 - 中文字幕在线免费看"
        description="你访问的影视条目不存在或已下架，请返回首页继续浏览中文字幕电影与电视剧。"
      />
      <div className="mx-auto flex max-w-[760px] flex-col items-center px-4 py-24 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-background-100">
          <i className="ri-file-warning-line text-2xl text-primary-600" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">没有找到这部作品</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          该影视条目可能已经调整或链接有误，你可以回到首页，或从分类频道重新挑选。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2.5">
          <Link to="/" className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700">返回首页</Link>
          <Link to="/category/movie" className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">浏览电影频道</Link>
        </div>
      </div>
    </Layout>
  );
}

function DetailContent({ item }: { item: Title }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const favorited = isFavorite(item.id);
  const related = getRelated(item, 6);
  const categoryPath = item.type === "movie" ? "/category/movie" : "/category/tv";
  const categoryName = item.type === "movie" ? "电影" : "电视剧";

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleFavorite = () => {
    toggleFavorite(item.id);
    setToast(favorited ? "已取消收藏" : "已加入收藏，本机浏览器可随时查看");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("播放页链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <Layout>
      <PageMeta
        title={`${item.title} - ${categoryName}在线观看 - 中文字幕在线免费看`}
        description={`${item.title}（${item.year}，${item.region}）${item.genres.join("、")}题材中文字幕${categoryName}。${item.summary.slice(0, 80)}`}
        keywords={`${item.title},${item.title}在线观看,中文字幕${categoryName},${item.genres.join(",")}`}
        type="video.movie"
      />

      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={item.poster}
          alt={`${item.title}背景剧照`}
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-30 blur-2xl"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/90 to-primary-950"></div>

        <div className="relative px-4 pb-8 pt-6 md:px-8 md:pb-10 md:pt-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-400">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <Link to={categoryPath} className="cursor-pointer transition-colors hover:text-accent-400">{categoryName}</Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <span className="text-background-200">{item.title}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-5 md:flex-row md:gap-8">
            <div className="w-[152px] shrink-0 md:w-[248px]">
              <div className="aspect-[3/4] overflow-hidden rounded-lg border border-background-50/10">
                <img
                  src={item.poster}
                  alt={`${item.title}中文字幕在线观看封面`}
                  title={`${item.title} - 中文字幕在线观看`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-semibold leading-snug text-background-50 md:text-4xl">
                {item.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-background-300 md:text-xs">
                <span className="rounded bg-accent-500 px-2 py-0.5 font-semibold text-primary-950">
                  {item.rating.toFixed(1)} 分
                </span>
                <span className="rounded border border-background-50/25 px-2 py-0.5">{item.year}</span>
                <span className="rounded border border-background-50/25 px-2 py-0.5">{item.region}</span>
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="cursor-pointer rounded border border-background-50/25 px-2 py-0.5 transition-colors hover:border-accent-400 hover:text-accent-300"
                  >
                    {genre}
                  </Link>
                ))}
                <span className="flex items-center gap-1 text-background-400">
                  <i className="ri-fire-fill text-sm text-accent-400" aria-hidden="true"></i>
                  {item.heat} 万次播放
                </span>
              </div>

              <p className="mt-4 max-w-[760px] text-[13px] leading-7 text-background-200">{item.summary}</p>

              <dl className="mt-4 max-w-[760px] border-t border-background-50/10 pt-2">
                <InfoRow label="导演" value={item.director} />
                <InfoRow label="主演" value={item.cast.join(" / ")} />
                <InfoRow label="语言" value={`${item.language}（中文字幕）`} />
                <InfoRow label="片长" value={item.duration} />
                {item.episodes ? <InfoRow label="集数" value={item.episodes} /> : null}
              </dl>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <a
                  href="#player"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-fill text-base" aria-hidden="true"></i>
                  立即观看
                </a>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm transition-colors ${
                    favorited
                      ? "border-accent-400 bg-accent-500/15 text-accent-300"
                      : "border-background-50/25 text-background-100 hover:border-accent-400 hover:text-accent-300"
                  }`}
                >
                  <i className={`${favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} text-base`} aria-hidden="true"></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/25 px-4 py-2.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-share-forward-line text-base" aria-hidden="true"></i>
                  分享链接
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="player" className="scroll-mt-20 px-4 py-8 md:px-8 md:py-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2.5 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
                <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
                在线播放（演示）
              </h2>
              <span className="rounded bg-background-100 px-2 py-1 text-[11px] text-foreground-600">中文字幕 · 高清示意</span>
            </div>
            <PlayerDemo title={item.title} />
          </div>

          <aside className="rounded-lg border border-background-200 bg-background-100 px-4 py-4">
            <h2 className="text-sm font-semibold text-foreground-950">观看提示</h2>
            <ul className="mt-3 space-y-2.5 text-xs leading-6 text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-sm text-primary-500" aria-hidden="true"></i>
                本页播放区为前端演示，暂未接入真实视频源。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-sm text-primary-500" aria-hidden="true"></i>
                收藏与分享功能可直接使用，收藏记录保存在本机浏览器。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-sm text-primary-500" aria-hidden="true"></i>
                站内不提供下载入口，如需观看完整作品请前往正版平台。
              </li>
            </ul>
            <div className="mt-4 border-t border-background-200 pt-3">
              <p className="text-[11px] text-foreground-500">所属分类</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Link
                  to={categoryPath}
                  className="cursor-pointer whitespace-nowrap rounded bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {categoryName}频道
                </Link>
                {item.genres.slice(0, 2).map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="cursor-pointer whitespace-nowrap rounded bg-accent-100 px-2.5 py-1 text-[11px] text-accent-900 transition-colors hover:bg-accent-200"
                  >
                    {genre}片单
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-8 md:px-8 md:py-10">
        <h2 className="flex items-center gap-2.5 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
          <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
          剧情简介
        </h2>
        <div className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4 md:px-5 md:py-5">
            <p className="text-[13px] leading-7 text-foreground-700">{item.summary}</p>
            <p className="mt-3 text-[13px] leading-7 text-foreground-700">
              《{item.title}》是一部 {item.year} 年出品的{item.region}
              {categoryName}，由{item.director}执导，{item.cast.join("、")}主演。全片以{item.language}
              呈现，本站整理为中文字幕版本，适合喜欢{item.genres.join("、")}题材的观众。
            </p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {item.genres.map((genre) => (
                <span key={genre} className="rounded bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600">
                  #{genre}
                </span>
              ))}
              <span className="rounded bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600">#中文字幕</span>
              <span className="rounded bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600">#高清在线观看</span>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4 md:px-5 md:py-5">
            <h3 className="text-sm font-semibold text-foreground-950">作品信息</h3>
            <dl className="mt-2">
              <div className="flex justify-between gap-3 border-b border-background-200 py-2 text-xs">
                <dt className="text-foreground-500">评分</dt>
                <dd className="font-medium text-foreground-900">{item.rating.toFixed(1)} / 10</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-background-200 py-2 text-xs">
                <dt className="text-foreground-500">热度</dt>
                <dd className="font-medium text-foreground-900">{item.heat} 万</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-background-200 py-2 text-xs">
                <dt className="text-foreground-500">上映年份</dt>
                <dd className="font-medium text-foreground-900">{item.year}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-background-200 py-2 text-xs">
                <dt className="text-foreground-500">地区</dt>
                <dd className="font-medium text-foreground-900">{item.region}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2 text-xs">
                <dt className="text-foreground-500">语言</dt>
                <dd className="font-medium text-foreground-900">中文字幕</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 md:py-10">
        <SectionHeader
          title="相关推荐"
          subtitle="同题材、同地区的其他作品，接着看下去"
          to={categoryPath}
          linkLabel={`更多${categoryName}`}
        />
        <PosterGrid items={related} />
      </section>

      <div
        className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary-950 px-4 py-2.5 text-xs text-background-100 transition-opacity duration-300 ${
          toast ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        role="status"
        aria-live="polite"
      >
        {toast}
      </div>
    </Layout>
  );
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getTitleById(id || "");

  if (!item) return <MissingDetail />;
  return <DetailContent item={item} />;
}